import { Component, OnDestroy, OnInit } from "@angular/core";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-azure-wafr-workloads",
  templateUrl: "./azure-wafr-workloads.component.html",
  styleUrls: ["./azure-wafr-workloads.component.css"],
})
export class AzureWafrWorkloadsComponent implements OnInit, OnDestroy {
  subscriptionId = localStorage.getItem("accountId") || "";
  userId = localStorage.getItem("selectedClientList");
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  loading = false;
  errorMessage: string = null;
  workloads: any[] = [];
  selectedWorkload: any = null;
  workloadSummary: any = null;
  workloadSummaryLoading = false;
  workloadSummaryError: string = null;
  selectedLens: any = null;
  selectedPillar: any = null;
  pillars: any[] = [];
  pillarsLoading = false;
  pillarsError: string = null;
  createSubscriptionId = "";
  createUserId = "";
  showCreateWorkloadModal = false;
  newWorkloadName = "";
  createWorkloadLoading = false;
  createErrorMessage: string = null;
  workloadNameError = false;
  currentMessage: any;
  destroyed = false;

  constructor(
    private apiService: APIService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.subscriptionId = localStorage.getItem("accountId") || "";

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      let data: any = {};
      try {
        data = JSON.parse(msg);
      } catch (err) {
        return;
      }
      if (data?.value == null) {
        return;
      }
      if (data.key == "accountId") {
        this.subscriptionId = data.value;
        this.load();
      }
    });

    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  private formatDateTime(value: string): string {
    if (!value) {
      return "-";
    }
    const dt = new Date(value);
    if (isNaN(dt.getTime())) {
      return value;
    }
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  private toPercent(value: any): string {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    const num = Number(value);
    if (isNaN(num)) {
      return `${value}`;
    }
    return `${Math.round(num * 100) / 100}%`;
  }

  private toAnsweredLabel(answered: any, total: any): string {
    const answeredNum =
      answered === null || answered === undefined || answered === ""
        ? null
        : Number(answered);
    const totalNum =
      total === null || total === undefined || total === "" ? null : Number(total);

    if (answeredNum === null && totalNum === null) {
      return "-";
    }

    const answeredText =
      answeredNum === null || isNaN(answeredNum) ? "-" : `${answeredNum}`;
    const totalText = totalNum === null || isNaN(totalNum) ? "-" : `${totalNum}`;
    return `${answeredText} / ${totalText}`;
  }

  private toDisplay(value: any): string {
    const text = this.toText(value).trim();
    return text ? text : "-";
  }

  getStatusClass(status: string): string {
    const value = (status || "").toUpperCase();
    if (value == "COMPLETED") {
      return "status-completed";
    }
    if (value == "FAILED" || value == "SCAN FAILED") {
      return "status-failed";
    }
    return "status-running";
  }

  isRunningStatus(status: string): boolean {
    const value = (status || "").toUpperCase();
    return value != "COMPLETED" && value != "FAILED" && value != "SCAN FAILED";
  }

  openCreateWorkloadModal() {
    this.showCreateWorkloadModal = true;
    this.newWorkloadName = "";
    this.createErrorMessage = null;
    this.workloadNameError = false;
  }

  closeCreateWorkloadModal() {
    this.showCreateWorkloadModal = false;
    this.newWorkloadName = "";
    this.createErrorMessage = null;
    this.workloadNameError = false;
    this.createWorkloadLoading = false;
  }

  private extractCreateDefaults(rows: any[]) {
    const userRow = rows.find((item: any) => this.isValidUserId(item?.user_id));

    this.createSubscriptionId =
      rows[0]?.subscription_id || this.subscriptionId || this.createSubscriptionId || "";

    this.createUserId =
      userRow?.user_id || this.userId || this.createUserId || "";
  }

  private isValidUserId(value: any): boolean {
    const user = (value || "").toString().trim();
    if (!user) {
      return false;
    }
    const lowered = user.toLowerCase();
    return lowered != "string" && lowered != "-";
  }

  private toText(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string") {
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return `${value}`;
    }
    if (Array.isArray(value)) {
      const values = value
        .map((item) => this.toText(item))
        .filter((item) => !!item);
      return values.join(", ");
    }
    if (typeof value === "object") {
      const nested =
        this.toText(value?.error_message) ||
        this.toText(value?.message) ||
        this.toText(value?.detail) ||
        this.toText(value?.error) ||
        this.toText(value?.last_error);
      if (nested) {
        return nested;
      }
      try {
        return JSON.stringify(value);
      } catch (err) {
        return `${value}`;
      }
    }
    return `${value}`;
  }

  private extractApiError(result: any): string {
    const text =
      this.toText(result?.error_message) ||
      this.toText(result?.message) ||
      this.toText(result?.detail) ||
      this.toText(result?.error) ||
      this.toText(result);
    return text || "Unable to create workload.";
  }

  openWorkloadSummary(workload: any) {
    if (!workload?.workload_id) {
      return;
    }
    this.selectedWorkload = workload;
    this.selectedLens = null;
    this.selectedPillar = null;
    this.pillars = [];
    this.pillarsError = null;
    this.loadWorkloadSummary(workload);
  }

  async backToWorkloads() {
    this.selectedWorkload = null;
    this.selectedLens = null;
    this.selectedPillar = null;
    this.pillars = [];
    this.pillarsError = null;
    this.pillarsLoading = false;
    this.workloadSummary = null;
    this.workloadSummaryError = null;
    this.workloadSummaryLoading = false;
    await this.load();
  }

  private formatPillarName(value: string): string {
    const key = (value || "").trim();
    if (!key) {
      return "-";
    }
    return key
      .replace(/_/g, " ")
      .replace(/\w\S*/g, (word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      });
  }

  openLens(lens: any) {
    this.selectedLens = lens;
    this.selectedPillar = null;
    this.loadPillars();
  }

  async backToLenses() {
    this.selectedLens = null;
    this.selectedPillar = null;
    this.pillars = [];
    this.pillarsError = null;
    this.pillarsLoading = false;
    if (this.selectedWorkload?.workload_id) {
      await this.loadWorkloadSummary(this.selectedWorkload);
    }
  }

  openPillar(pillar: any) {
    this.selectedPillar = pillar;
  }

  backToPillars() {
    this.selectedPillar = null;
    this.loadPillars();
  }

  onQuestionAnswerSaved(result: any) {
    if (!result || !this.selectedPillar?.pillar) {
      return;
    }

    const patchPillarRow = (pillarRow: any) => {
      if (!pillarRow) {
        return;
      }
      pillarRow.answered_questions =
        result?.answered_questions ?? pillarRow.answered_questions;
      pillarRow.manual_question_count =
        result?.manual_question_count ?? pillarRow.manual_question_count;
      pillarRow.score_percent =
        result?.pillar_score_percent ?? pillarRow.score_percent;
      pillarRow.score_label = this.toPercent(pillarRow.score_percent);
      pillarRow.answered_label = this.toAnsweredLabel(
        pillarRow.answered_questions,
        pillarRow.total_questions
      );
    };

    patchPillarRow(this.selectedPillar);

    const matched = this.pillars.find(
      (pillar: any) => pillar?.pillar == this.selectedPillar?.pillar
    );
    patchPillarRow(matched);
  }

  async loadPillars() {
    if (!this.selectedWorkload?.workload_id) {
      this.pillarsError = "Workload ID is missing.";
      return;
    }

    const subscriptionId = this.toText(
      this.selectedWorkload?.subscription_id ||
        this.subscriptionId ||
        localStorage.getItem("accountId")
    ).trim();

    if (!subscriptionId) {
      this.pillarsError = "Subscription ID is missing.";
      return;
    }

    this.pillarsLoading = true;
    this.pillarsError = null;
    this.pillars = [];

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads/` +
      `${encodeURIComponent(this.selectedWorkload.workload_id)}/pillars` +
      `?include_question_definitions=true&subscription_id=${encodeURIComponent(
        subscriptionId
      )}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (this.destroyed) {
        return;
      }

      if (result?.workload_id && Array.isArray(result?.pillars)) {
        this.pillars = result.pillars
          .map((pillar: any) => ({
            ...pillar,
            pillar_name: this.formatPillarName(pillar?.pillar),
            score_label: this.toPercent(pillar?.score_percent),
            answered_label: this.toAnsweredLabel(
              pillar?.answered_questions,
              pillar?.total_questions
            ),
            updated_at_label: this.formatDateTime(pillar?.updated_at),
            questions: Array.isArray(pillar?.questions) ? pillar.questions : [],
          }))
          .sort((a: any, b: any) => {
            const aOrder =
              a?.questions?.[0]?.nav_order !== undefined ? a.questions[0].nav_order : 9999;
            const bOrder =
              b?.questions?.[0]?.nav_order !== undefined ? b.questions[0].nav_order : 9999;
            return aOrder - bOrder;
          });
      } else {
        this.pillarsError = this.extractApiError(result);
      }
    } catch (err: any) {
      this.pillarsError = this.extractApiError(err);
    } finally {
      this.pillarsLoading = false;
    }
  }

  async loadWorkloadSummary(workload: any) {
    this.workloadSummaryLoading = true;
    this.workloadSummaryError = null;
    this.workloadSummary = null;

    const subscriptionId = this.toText(
      workload?.subscription_id || this.subscriptionId || localStorage.getItem("accountId")
    ).trim();

    if (!subscriptionId) {
      this.workloadSummaryLoading = false;
      this.workloadSummaryError = "Subscription ID is missing.";
      return;
    }

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads/` +
      `${encodeURIComponent(workload.workload_id)}/summary?subscription_id=` +
      `${encodeURIComponent(subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (this.destroyed) {
        return;
      }

      if (result?.workload_id) {
        const lensRows = result?.lens
          ? [
              {
                lens_name: this.toDisplay(result?.lens?.lens_name),
                lens_key: this.toDisplay(result?.lens?.lens_key),
                answered_label: this.toAnsweredLabel(
                  result?.lens?.answered_questions,
                  result?.lens?.total_questions
                ),
                high_risk_count: this.toDisplay(result?.lens?.high_risk_count),
                medium_risk_count: this.toDisplay(result?.lens?.medium_risk_count),
              },
            ]
          : [];

        this.workloadSummary = {
          workload_id: this.toDisplay(result?.workload_id),
          workload_name: this.toDisplay(result?.workload_name),
          subscription_id: this.toDisplay(result?.subscription_id),
          status: this.toDisplay(result?.status),
          lenses: lensRows,
        };
      } else {
        this.workloadSummaryError = this.extractApiError(result);
      }
    } catch (err: any) {
      this.workloadSummaryError = this.extractApiError(err);
    } finally {
      this.workloadSummaryLoading = false;
    }
  }

  private async refreshCreateDefaultsFromApi() {
    const subscriptionId = this.subscriptionId || localStorage.getItem("accountId") || "";
    if (!subscriptionId) {
      return;
    }

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads` +
      `?limit=50&subscription_id=${encodeURIComponent(subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (Array.isArray(result?.workloads) && result.workloads.length > 0) {
        this.extractCreateDefaults(result.workloads);
      }
    } catch (err) {
      // Keep existing fallback values from already loaded data.
    }
  }

  validateWorkloadName(): boolean {
    const name = (this.newWorkloadName || "").trim();
    if (!name) {
      this.workloadNameError = true;
      this.createErrorMessage = "Workload Name is a mandatory field.";
      return false;
    }
    if (name.length < 3 || name.length > 100) {
      this.workloadNameError = true;
      this.createErrorMessage =
        "Workload Name should be at least 3 characters and at most 100 characters.";
      return false;
    }
    this.workloadNameError = false;
    return true;
  }

  async createWorkload() {
    this.createErrorMessage = null;

    if (!this.validateWorkloadName()) {
      return;
    }

    this.createWorkloadLoading = true;
    await this.refreshCreateDefaultsFromApi();

    const subscriptionId = this.toText(
      this.createSubscriptionId || this.subscriptionId || localStorage.getItem("accountId")
    );
    const userId = this.toText(
      this.createUserId || this.userId || localStorage.getItem("eId")
    );

    if (!subscriptionId || !this.isValidUserId(userId)) {
      this.createErrorMessage =
        "Unable to create workload. Subscription ID or User ID is missing.";
      this.createWorkloadLoading = false;
      return;
    }

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const payload = {
      workload_name: this.newWorkloadName.trim(),
      subscription_id: subscriptionId.trim(),
      user_id: userId.trim(),
      questionnaire_version: "",
    };

    const apiUrl = `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads`;

    try {
      const result: any = await this.apiService.postDataPromisNew(
        apiUrl,
        payload,
        headers
      );
      if (this.destroyed) {
        return;
      }

      const status = (result?.status || "").toString().toUpperCase();
      const isQueuedLikeStatus =
        status == "QUEUED" || status == "IN_PROGRESS" || status == "COMPLETED";
      const createSuccess = !!(result?.workload_id || result?.scan_job_id || isQueuedLikeStatus);

      if (createSuccess) {
        const successMessage =
          this.toText(result?.message) || "Workload created successfully.";
        this.notifier.alert("success", "", successMessage, "success", 5000);
        this.closeCreateWorkloadModal();
        await this.load();
        return;
      }

      this.createErrorMessage = this.extractApiError(result);
      this.notifier.alert("Info", "", this.createErrorMessage, "info", 5000);
    } catch (err: any) {
      this.createErrorMessage = this.extractApiError(err);
      this.notifier.alert("Info", "", this.createErrorMessage, "info", 5000);
    } finally {
      this.createWorkloadLoading = false;
    }
  }

  async load() {
    this.loading = true;
    this.errorMessage = null;
    this.workloads = [];
    this.workloadSummary = null;
    this.workloadSummaryError = null;
    this.selectedLens = null;
    this.selectedPillar = null;
    this.pillars = [];
    this.pillarsError = null;
    this.pillarsLoading = false;

    if (!this.subscriptionId) {
      this.loading = false;
      this.errorMessage = "Subscription ID is missing.";
      return;
    }

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads` +
      `?limit=50&subscription_id=${encodeURIComponent(this.subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (this.destroyed) {
        return;
      }

      if (Array.isArray(result?.workloads)) {
        this.extractCreateDefaults(result.workloads);

        const rows = result.workloads.map((workload: any) => ({
          ...workload,
          workload_name: workload?.workload_name || "-",
          user_id: workload?.user_id || "-",
          status: workload?.status || "-",
          created_at_label: this.formatDateTime(workload?.created_at),
          updated_at_label: this.formatDateTime(workload?.updated_at),
          overall_score_label: this.toPercent(workload?.overall_score_percent),
          answered_label: this.toAnsweredLabel(
            workload?.answered_questions,
            workload?.total_questions
          ),
          high_risk_count:
            workload?.high_risk_count === 0 || workload?.high_risk_count
              ? workload?.high_risk_count
              : "-",
          medium_risk_count:
            workload?.medium_risk_count === 0 || workload?.medium_risk_count
              ? workload?.medium_risk_count
              : "-",
          last_error: workload?.last_error || "-",
        }));

        this.workloads = rows.sort((a: any, b: any) => {
          const aTime = new Date(a?.updated_at || a?.created_at || 0).getTime();
          const bTime = new Date(b?.updated_at || b?.created_at || 0).getTime();
          return bTime - aTime;
        });

        if (this.selectedWorkload?.workload_id) {
          const matched = this.workloads.find(
            (item: any) => item.workload_id == this.selectedWorkload.workload_id
          );
          if (matched) {
            this.selectedWorkload = matched;
          } else {
            this.backToWorkloads();
          }
        }
      } else {
        this.errorMessage =
          result?.error_message || result?.message || "Unable to load workloads.";
      }
    } catch (err: any) {
      this.errorMessage = err?.toString() || "Unable to load workloads.";
    } finally {
      this.loading = false;
    }
  }
}
